import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { notify } from "@/lib/notify";

import { authApi, type LoginResult } from "@/lib/api/auth";
import { ApiRequestError } from "@/lib/api/client";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/Button";
import { Input, PasswordInput } from "@/components/ui/Input";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { MFASetupPanel } from "./MFASetupPanel";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  tenant_slug: z.string().optional(),
  mfa_code: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

/**
 * The email/password/MFA-code sign-in form shared by LoginPage (every
 * tenant employee, plus a Super Admin signing in with credentials there)
 * and SuperAdminLoginPage's "Login with Credentials" tab - extracted so the
 * MFA-retry/MFA-setup logic exists in exactly one place. Stores the session
 * and shows the welcome toast itself; onSuccess only handles navigation,
 * which differs between the two callers.
 */
export function CredentialsLoginForm({ onSuccess }: { onSuccess: (result: LoginResult) => void }) {
  const setSession = useAuthStore((s) => s.setSession);
  const [needsWorkspace, setNeedsWorkspace] = useState(false);
  const [needsMFACode, setNeedsMFACode] = useState(false);
  // Set only when a login attempt reports mfa_setup_required: true - the
  // account's MFA is required but not yet enrolled. access_token here is a
  // short-lived MFA-setup token (security.TokenMFASetup on the backend),
  // never treated as a real session (setSession is never called for it).
  const [mfaSetup, setMFASetup] = useState<LoginResult | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    // This form already shows its own persistent inline ErrorMessage banner
    // (below) instead of a transient toast - a login failure is exactly the
    // "handles its own failure UI" case the global handler's opt-out exists
    // for, so this mutation skips the automatic error toast to avoid saying
    // the same thing twice.
    meta: { silent: true },
    mutationFn: authApi.login,
    onSuccess: (result) => {
      if (result.mfa_setup_required) {
        setMFASetup(result);
        return;
      }
      setSession({
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        user: result.user,
        tenant: result.tenant,
      });
      notify.success(`Welcome back, ${result.user.first_name}!`);
      onSuccess(result);
    },
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        if (error.appError.code === "INVALID_INPUT" && error.appError.fields?.some((f) => f.field === "tenant_slug")) {
          setNeedsWorkspace(true);
          return;
        }
        if (error.appError.fields?.some((f) => f.field === "mfa_code")) {
          setNeedsMFACode(true);
          return;
        }
        for (const field of error.appError.fields ?? []) {
          if (field.field === "email" || field.field === "password" || field.field === "tenant_slug") {
            setError(field.field, { message: field.message });
          }
        }
      }
    },
  });

  if (mfaSetup) {
    return (
      <MFASetupPanel
        setupToken={mfaSetup.access_token}
        onComplete={() => {
          notify.success("Multi-factor authentication enabled. Sign in again to continue.");
          setMFASetup(null);
          setNeedsMFACode(true);
        }}
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit(
        (values) => mutation.mutate(values),
        () => notify.warning("Please fill all required fields."),
      )}
      className="flex flex-col gap-4"
    >
      <Input
        id="credentials-email"
        label="Email"
        type="email"
        autoComplete="username"
        placeholder="you@company.com"
        error={errors.email?.message}
        {...register("email")}
      />
      <PasswordInput
        id="credentials-password"
        label="Password"
        autoComplete="current-password"
        placeholder="••••••••"
        error={errors.password?.message}
        {...register("password")}
      />
      {needsWorkspace && (
        <Input
          id="credentials-tenant-slug"
          label="Workspace"
          placeholder="acme-manufacturing"
          hint="This email is registered with more than one workspace. Enter its identifier from the URL your admin shared."
          error={errors.tenant_slug?.message}
          {...register("tenant_slug")}
        />
      )}
      {needsMFACode && (
        <Input
          id="credentials-mfa-code"
          label="Authentication code"
          placeholder="123456"
          hint="Enter the code from your authenticator app, or one of your backup codes."
          autoComplete="one-time-code"
          error={errors.mfa_code?.message}
          {...register("mfa_code")}
        />
      )}

      {mutation.isError && !needsWorkspace && !needsMFACode && <ErrorMessage error={mutation.error} />}
      {mutation.isError && needsMFACode && getValues("mfa_code") && <ErrorMessage error={mutation.error} />}

      <Button type="submit" loading={mutation.isPending} fullWidth className="mt-1">
        Sign in
      </Button>
    </form>
  );
}
